// Thumbnails for the agent's chat list: the newest generation of each chat.
//
// One presign per finished job, cached for the SPA session (presigned URLs
// last 2 h; entries are dropped after 90 min). Videos only ever show a still:
// the stored thumbnail, generated on demand via ensure-thumbnail when a video
// has none — never a <video> element per row. A job that is still running,
// failed or was deleted resolves to null and is asked again later.

import { useEffect, useState } from 'react';
import type { GenerationKind } from './agent';

export type ThumbJob = { activity_id: number; kind: GenerationKind };

const TTL_MS = 90 * 60_000;
export const ENSURE_WAIT_MS = 4_000;

type Presigned = { urls?: string[]; thumbnail_url?: string | null; thumbnailUrl?: string };

export class ThumbCache {
  private entries = new Map<number, { at: number; p: Promise<string | null> }>();

  constructor(
    private readonly fetchFn: typeof fetch = (...args) => fetch(...args),
    private readonly wait: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms))
  ) {}

  get(job: ThumbJob): Promise<string | null> {
    const hit = this.entries.get(job.activity_id);
    if (hit && Date.now() - hit.at < TTL_MS) return hit.p;
    const p = this.load(job).then((url) => {
      if (!url) this.entries.delete(job.activity_id);
      return url;
    });
    this.entries.set(job.activity_id, { at: Date.now(), p });
    return p;
  }

  private async presign(id: number): Promise<Presigned | null> {
    try {
      const res = await this.fetchFn(`/api/v1/history/${id}/presigned-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      return res.ok ? ((await res.json()) as Presigned) : null;
    } catch {
      return null;
    }
  }

  private async load(job: ThumbJob): Promise<string | null> {
    const first = await this.presign(job.activity_id);
    if (!first) return null;
    if (job.kind !== 'video') return first.urls?.[0] ?? null;
    const still = (d: Presigned | null) => d?.thumbnail_url ?? d?.thumbnailUrl ?? null;
    if (still(first)) return still(first);
    await this.fetchFn(`/api/v1/history/${job.activity_id}/ensure-thumbnail`, { method: 'POST', credentials: 'include' }).catch(() => null);
    await this.wait(ENSURE_WAIT_MS);
    return still(await this.presign(job.activity_id));
  }
}

export const agentThumbs = new ThumbCache();

// The chat's thumbnail URL, once its newest job has finished.
export function useConversationThumb(job: ThumbJob | undefined, running: boolean): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const id = job?.activity_id;
  const kind = job?.kind;
  useEffect(() => {
    setUrl(null);
    if (id == null || !kind || running) return;
    let live = true;
    void agentThumbs.get({ activity_id: id, kind }).then((u) => {
      if (live) setUrl(u);
    });
    return () => {
      live = false;
    };
  }, [id, kind, running]);
  return url;
}
